import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UpdateVerificationAPI } from "@/services/api/student/updateVerification";
import { RejectVerificationAPI } from "@/services/api/student/rejectVerification";
import Icon from "./Icon";
import FailReasonModal from "./FailReasonModal";
import Modal from "./Modal";

type VerificationDocument = "student_card" | "hkid";

/**
 * Fallback order for call sites that do not name a document: HKID first, as it
 * has always been laid out.
 */
const DOCUMENT_ORDER: VerificationDocument[] = ["hkid", "student_card"];

interface ModalVerifyDocumentsProps {
  visible: boolean;
  onClose: () => void;
  onApprove: (document?: "student_card" | "hkid") => void;
  onReject: (document?: "student_card" | "hkid") => void;
  studentCardImageUrl?: string;
  hkidImageUrl?: string;
  initialStudentCardNumber?: string;
  initialStudentCardName?: string;
  initialHKIDNumber?: string;
  initialHKIDName?: string;
  student_id?: string;
  scUploaded?: boolean;
  hkidUploaded?: boolean;
  scVerified?: boolean;
  hkidVerified?: boolean;
  /**
   * The document under review. The admin reaches this dialog through one button
   * per document, so naming one here renders that document alone — the admin
   * approves exactly what they tapped instead of scrolling past a second document
   * they did not ask for. Left null (older call sites) both documents are shown.
   */
  document?: VerificationDocument | null;
}

/**
 * Admin verification dialog: shows the document the admin came to review, lets
 * them correct the extracted fields, then approve (PATCH updateVerification) or
 * reject (POST rejectVerification with a reason). The action carries the
 * document, so it can only ever write that document's columns. Omitting
 * `document` restores the legacy dialog that shows both documents side by side,
 * each with its own actions.
 */
const ModalVerifyDocuments: React.FC<ModalVerifyDocumentsProps> = ({
  visible,
  onClose,
  onApprove,
  onReject,
  studentCardImageUrl,
  hkidImageUrl,
  initialStudentCardNumber = "",
  initialStudentCardName = "",
  initialHKIDNumber = "",
  initialHKIDName = "",
  student_id = "",
  scUploaded = false,
  hkidUploaded = false,
  scVerified = false,
  hkidVerified = false,
  document: activeDocument = null,
}) => {
  const { t } = useTranslation();
  const [studentCardNumber, setStudentCardNumber] = useState(initialStudentCardNumber);
  const [studentCardName, setStudentCardName] = useState(initialStudentCardName);
  const [hkidNumber, setHKIDNumber] = useState(initialHKIDNumber);
  const [hkidName, setHKIDName] = useState(initialHKIDName);
  const [failModalVisible, setFailModalVisible] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<VerificationDocument | null>(null);

  useEffect(() => {
    setStudentCardNumber(initialStudentCardNumber);
    setStudentCardName(initialStudentCardName);
    setHKIDNumber(initialHKIDNumber);
    setHKIDName(initialHKIDName);
  }, [initialStudentCardNumber, initialStudentCardName, initialHKIDNumber, initialHKIDName, visible]);

  const handleApprove = async (document: VerificationDocument) => {
    const resp = await UpdateVerificationAPI({
      student_card_number: studentCardNumber,
      student_card_name: studentCardName,
      hkid_number: hkidNumber,
      hkid_name: hkidName,
      student_id,
      document,
    });
    if (resp.status === 0) {
      onApprove(document);
      onClose();
    }
  };

  const sectionFor = (document: VerificationDocument) =>
    document === "hkid"
      ? {
          document,
          label: t("HKID"),
          imageUrl: hkidImageUrl,
          uploaded: hkidUploaded,
          verified: hkidVerified,
          number: hkidNumber,
          name: hkidName,
          numberPlaceholder: t("HKID Number"),
          namePlaceholder: t("HKID Name"),
          missingText: t("Student has not uploaded HKID"),
          onNumberChange: setHKIDNumber,
          onNameChange: setHKIDName,
        }
      : {
          document,
          label: t("Student Card"),
          imageUrl: studentCardImageUrl,
          uploaded: scUploaded,
          verified: scVerified,
          number: studentCardNumber,
          name: studentCardName,
          numberPlaceholder: t("Card Number"),
          namePlaceholder: t("Card Name"),
          missingText: t("Student has not uploaded student card"),
          onNumberChange: setStudentCardNumber,
          onNameChange: setStudentCardName,
        };

  // Only the named document is built at all, so the dialog can never show the
  // other one — not its image, not its fields, not a placeholder for it.
  const shown = (activeDocument ? [activeDocument] : DOCUMENT_ORDER).map(sectionFor);

  const title =
    activeDocument === "hkid"
      ? t("Verify HKID")
      : activeDocument === "student_card"
        ? t("Verify Student Card")
        : t("Verify Documents");

  return (
    <>
      <Modal visible={visible} onClose={onClose}>
        <div className="max-h-[85vh] overflow-y-auto rounded-[18px] bg-white p-7 shadow-lg">
          <div className="flex flex-col items-center">
            <Icon iconType="Feather" name="file-text" size={44} color="#ffa217" />
            <div className="pt-5 text-center text-lg font-bold tracking-wide text-[#ffa217]">
              {title}
            </div>

            {shown.map((section) => (
              <div key={section.document} className="mb-4 flex w-full flex-col items-center">
                {/* The title already names the document; only the two-document
                    fallback needs a heading per section. */}
                {shown.length > 1 && (
                  <div className="mb-2 self-start text-[15px] font-semibold text-[#ffa217]">
                    {section.label}
                  </div>
                )}
                {section.imageUrl ? (
                  <img
                    src={section.imageUrl}
                    alt={section.label}
                    className="h-[180px] w-[200px] rounded-lg bg-[#eee] object-contain"
                  />
                ) : (
                  <>
                    <div className="mt-10 text-[13px] text-[#aaa]">{t("No Image")}</div>
                    <div className="mt-1 text-[12px] text-[#e08f0e]">
                      {section.missingText}
                    </div>
                  </>
                )}
                <input
                  className="mt-2 mb-0.5 w-[90%] rounded-md border border-[#ccc] bg-white px-2.5 py-1.5 text-sm text-black"
                  placeholder={section.numberPlaceholder}
                  value={section.number}
                  onChange={(e) => section.onNumberChange(e.target.value)}
                />
                <input
                  className="mt-2 mb-0.5 w-[90%] rounded-md border border-[#ccc] bg-white px-2.5 py-1.5 text-sm text-black"
                  placeholder={section.namePlaceholder}
                  value={section.name}
                  onChange={(e) => section.onNameChange(e.target.value)}
                />
                {section.uploaded && !section.verified && (
                  <div className="mt-2 flex w-[90%] justify-end gap-2">
                    <button
                      type="button"
                      className="rounded-lg bg-[#ffa217] px-3 py-1.5 text-xs font-semibold text-white transition hover:opacity-90"
                      onClick={() => handleApprove(section.document)}
                    >
                      {t("Approve")}
                    </button>
                    <button
                      type="button"
                      className="rounded-lg bg-[#dc3545] px-3 py-1.5 text-xs font-semibold text-white transition hover:opacity-90"
                      onClick={() => {
                        setRejectTarget(section.document);
                        setFailModalVisible(true);
                      }}
                    >
                      {t("Reject")}
                    </button>
                  </div>
                )}
              </div>
            ))}

            <div className="mt-4 flex w-full justify-between gap-3">
              <button
                type="button"
                className="flex-1 rounded-lg border border-[#ffa217] bg-white py-3.5 text-base font-bold text-[#ffa217] transition hover:bg-[#fff8f0]"
                onClick={onClose}
              >
                {t("Close")}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <FailReasonModal
        visible={failModalVisible}
        onClose={() => {
          setFailModalVisible(false);
          setRejectTarget(null);
        }}
        loading={rejectLoading}
        title={t("Reject Document")}
        onSubmit={async (reason: string) => {
          setRejectLoading(true);
          try {
            const resp = await RejectVerificationAPI({
              student_id: student_id || "",
              reject_reason: reason,
              document: rejectTarget || undefined,
            });
            if (resp.status === 0) {
              setFailModalVisible(false);
              onReject(rejectTarget || undefined);
              onClose();
              setRejectTarget(null);
            }
          } finally {
            setRejectLoading(false);
          }
        }}
      />
    </>
  );
};

export default ModalVerifyDocuments;